import { PANOPLY_ORDER, UNIQUES, type HeroClass, type UniqueId } from '@somnhold/content';
import type { PanopliaLook } from '@somnhold/render';
import { hexToWorld, parseHexKey, uniquePieces, worldToHex, type CampaignState } from '@somnhold/sim';

/**
 * Where the armory's rack stands (docs/EQUIPO.md §6): beside the forge, on the first spot around it whose cell is
 * free, the table camera's side first (in front, then the sides, then behind). Sim units.
 */
const ARMERO_SPOTS: [number, number][] = [[-330, 520], [330, 520], [-600, 0], [600, 0], [-330, -520], [330, -520]];
export const armeroSpot = (s: CampaignState, forgeKey: string): { x: number; y: number } => {
  const f = parseHexKey(forgeKey);
  const c = hexToWorld(f.q, f.r);
  for (const [dx, dy] of ARMERO_SPOTS) {
    const h = worldToHex(c.x + dx, c.y + dy);
    const k = `${h.q},${h.r}`;
    if (k !== forgeKey && !s.cells[k]?.built && !s.cells[k]?.job && !(h.q === 0 && h.r === 0)) return { x: c.x + dx, y: c.y + dy };
  }
  return { x: c.x + (ARMERO_SPOTS[0] as [number, number])[0], y: c.y + (ARMERO_SPOTS[0] as [number, number])[1] };
};

/**
 * What the old barracks' panoply over the rack shows (docs/PANOPLIA.md §9.6): every rearmed piece a hero of the group
 * holds (those still in the campaign), each once with the highest slot stage among its owners, the highest stages
 * first and then the newest; twelve at most. The count is against the panoply pieces of the group's classes today.
 * Null when nobody has rearmed one.
 */
export const panopliaLook = (s: CampaignState): PanopliaLook | null => {
  const best = new Map<UniqueId, { stage: number; item: number }>();
  const classes = new Set<HeroClass>();
  for (const p of s.players) {
    if (p.left) continue;
    classes.add(p.hero.heroClass);
    for (const it of uniquePieces(p.hero)) {
      const id = it.unique;
      if (!id || !UNIQUES[id].rearmed) continue;
      const was = best.get(id);
      const stage = it.awaken ?? 0;
      if (!was || stage > was.stage || (stage === was.stage && it.id > was.item)) best.set(id, { stage, item: Math.max(it.id, was?.item ?? 0) });
    }
  }
  // What the group rearmed stays hung, its hero gone or not (docs/PANOPLIA.md §9.6).
  for (const x of s.hung ?? []) {
    const was = best.get(x.id);
    if (!was) best.set(x.id, { stage: x.stage, item: x.item });
    else if (x.stage > was.stage) was.stage = x.stage;
    classes.add(x.cls);
  }
  if (!best.size) return null;
  const pieces = [...best]
    .sort((a, b) => b[1].stage - a[1].stage || b[1].item - a[1].item)
    .slice(0, 12)
    .map(([id, v]) => ({ id, stage: v.stage }));
  const total = PANOPLY_ORDER.filter((id) => classes.has(UNIQUES[id].cls)).length;
  return { pieces, held: best.size, total };
};
